import React from 'react';
import { useCommunityContext } from '@/providers/community-context';
import { Button } from '@/components/ui/button';
import { Megaphone, Plus, Loader2, Edit, LineChart } from 'lucide-react';
import axiosInstance from '@/lib/axios';
import useSWR from 'swr';
import { useNavigate } from 'react-router-dom';
import type { Advertisement, AdvertisementCampaignSummary } from '@/types';

const PLACEMENT_LABELS: Record<string, string> = {
  DASHBOARD: 'Dashboard Banner',
  POPUP: 'Popup',
};

function getCampaignStatus(campaign: AdvertisementCampaignSummary) {
  if (!campaign.isActive) return { label: 'Inactive', color: 'bg-zinc-500/90' };

  const now = new Date();
  if (campaign.startDate && new Date(campaign.startDate) > now) {
    return { label: 'Scheduled', color: 'bg-blue-500/90' };
  }
  if (campaign.endDate && new Date(campaign.endDate) < now) {
    return { label: 'Ended', color: 'bg-orange-500/90' };
  }

  return { label: 'Active', color: 'bg-green-500/90' };
}

function formatDateTime(dateStr: string) {
  const d = new Date(dateStr);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

export default function AdvertisementPage() {
  const { community } = useCommunityContext();
  const navigate = useNavigate();

  const { data, isLoading: isFetching } = useSWR(
    community?.id ? `/api/communities/${community.id}/advertisements` : null,
    (url) => axiosInstance.get(url).then((res) => res.data)
  );

  const advertisements: Advertisement[] = data?.advertisements ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Advertisements</h1>
          <p className="text-muted-foreground">
            Create ads and run multiple campaigns per ad for {community?.name}
          </p>
        </div>
        <Button
          className="gap-2 rounded-xl"
          onClick={() => navigate(`/${community?.id}/advertisement/new`)}
        >
          <Plus className="h-4 w-4" /> New Ad
        </Button>
      </div>

      {isFetching ? (
        <div className="flex justify-center p-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : advertisements.length > 0 ? (
        <div className="space-y-6 mt-8">
          {advertisements.map((ad) => (
            <div
              key={ad.id}
              className="glass-card rounded-2xl border border-border/50 overflow-hidden"
            >
              <div className="flex flex-col md:flex-row gap-0">
                <div className="md:w-64 shrink-0 aspect-video md:aspect-[4/3] bg-muted overflow-hidden relative">
                  {ad.campaigns?.[0]?.horizontalMediaUrl || ad.campaigns?.[0]?.verticalMediaUrl ? (
                    <img
                      src={
                        ad.campaigns[0].horizontalMediaUrl || ad.campaigns[0].verticalMediaUrl || ''
                      }
                      alt={ad.title}
                      className="w-full h-full object-cover absolute inset-0"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground/50 absolute inset-0">
                      No media yet
                    </div>
                  )}
                </div>
                <div className="flex-1 p-5 flex flex-col">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
                    <div>
                      <h3 className="font-bold text-xl text-foreground">{ad.title}</h3>
                      {ad.description && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                          {ad.description}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/${community?.id}/advertisement/ad/${ad.id}/edit`)}
                      >
                        <Edit className="h-4 w-4 mr-1.5" />
                        Edit Ad
                      </Button>
                      <Button
                        size="sm"
                        onClick={() =>
                          navigate(`/${community?.id}/advertisement/ad/${ad.id}/campaign/new`)
                        }
                      >
                        <Plus className="h-4 w-4 mr-1.5" />
                        Add Campaign
                      </Button>
                    </div>
                  </div>

                  {(ad.campaigns?.length ?? 0) > 0 ? (
                    <div className="space-y-2 mt-auto">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Campaigns ({ad.campaigns!.length})
                      </p>
                      <div className="space-y-2">
                        {ad.campaigns!.map((campaign) => {
                          const status = getCampaignStatus(campaign);
                          return (
                            <div
                              key={campaign.id}
                              className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-border/40 bg-background/50 px-4 py-3"
                            >
                              <div className="space-y-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="text-sm font-medium">
                                    {PLACEMENT_LABELS[campaign.type] ?? campaign.type}
                                  </span>
                                  <span
                                    className={`${status.color} text-white text-xs px-2 py-0.5 rounded-full font-semibold`}
                                  >
                                    {status.label}
                                  </span>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                  {campaign.startDate
                                    ? `From ${formatDateTime(campaign.startDate)}`
                                    : 'Starts immediately'}
                                  {campaign.endDate
                                    ? ` · Until ${formatDateTime(campaign.endDate)}`
                                    : ' · No end date'}
                                </p>
                              </div>
                              <div className="flex gap-2 shrink-0">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    navigate(
                                      `/${community?.id}/advertisement/campaign/${campaign.id}/edit`
                                    )
                                  }
                                >
                                  <Edit className="h-3.5 w-3.5 mr-1" />
                                  Edit
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    navigate(
                                      `/${community?.id}/advertisement/campaign/${campaign.id}/stats`
                                    )
                                  }
                                >
                                  <LineChart className="h-3.5 w-3.5 mr-1" />
                                  Stats
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground mt-auto">
                      No campaigns yet. Add a campaign to show this ad on the dashboard or as a
                      popup.
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="glass rounded-xl border border-border/10 p-12 text-center mt-8">
          <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Megaphone className="h-8 w-8 text-primary opacity-80" />
          </div>
          <h3 className="text-lg font-bold text-foreground">No Advertisements</h3>
          <p className="text-sm text-muted-foreground mt-2 max-w-sm mx-auto">
            Create an ad first, then add one or more campaigns to control where and when it appears.
          </p>
        </div>
      )}
    </div>
  );
}
